import React, { useState } from 'react';
import { X, Send, Mail } from 'lucide-react';
import apiService from '../../services/api';
import { SendCompanyEmail } from '../../types';
import toast from 'react-hot-toast';
import { PRODUCT_NAME } from '../../constants/app';

interface EmailCompanyModalProps {
  companyId: number;
  companyName: string;
  isOpen: boolean;
  onClose: () => void;
}

const EmailCompanyModal: React.FC<EmailCompanyModalProps> = ({
  companyId,
  companyName,
  isOpen,
  onClose
}) => {
  const [emailData, setEmailData] = useState<SendCompanyEmail>({
    subject: '',
    body: '',
    additionalRecipients: []
  });
  const [sending, setSending] = useState(false);
  const [additionalRecipient, setAdditionalRecipient] = useState('');

  const emailTemplates = [
    { id: 'welcome', name: 'Welcome', subject: `Welcome to ${PRODUCT_NAME}`, body: `Dear Admin,\n\nWelcome to ${PRODUCT_NAME}! We're excited to have you on board.\n\nBest regards,\n${PRODUCT_NAME} Team` },
    { id: 'feature', name: 'Feature Announcement', subject: 'New Feature Available', body: `Dear Admin,\n\nWe're excited to announce a new feature...\n\nBest regards,\n${PRODUCT_NAME} Team` },
    { id: 'support', name: 'Support Follow-up', subject: 'Follow-up on Your Support Request', body: `Dear Admin,\n\nWe wanted to follow up on your recent support request...\n\nBest regards,\n${PRODUCT_NAME} Team` },
    { id: 'billing', name: 'Billing Inquiry', subject: 'Billing Information', body: `Dear Admin,\n\nWe wanted to reach out regarding your billing...\n\nBest regards,\n${PRODUCT_NAME} Team` }
  ];

  const handleTemplateSelect = (template: typeof emailTemplates[0]) => {
    setEmailData({
      ...emailData,
      subject: template.subject,
      body: template.body
    });
  };

  const handleAddRecipient = () => {
    if (additionalRecipient.trim() && emailData.additionalRecipients) {
      setEmailData({
        ...emailData,
        additionalRecipients: [...emailData.additionalRecipients, additionalRecipient.trim()]
      });
      setAdditionalRecipient('');
    }
  };

  const handleRemoveRecipient = (email: string) => {
    if (emailData.additionalRecipients) {
      setEmailData({
        ...emailData,
        additionalRecipients: emailData.additionalRecipients.filter(e => e !== email)
      });
    }
  };

  const handleSend = async () => {
    if (!emailData.subject.trim() || !emailData.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }

    setSending(true);
    try {
      const response = await apiService.sendEmailToCompanyAdmin(companyId, emailData);
      if (response.success) {
        toast.success('Email sent successfully');
        setEmailData({ subject: '', body: '', additionalRecipients: [] });
        setAdditionalRecipient('');
        onClose();
      } else {
        toast.error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Send Email to {companyName} Admins
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Templates
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {emailTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Email subject"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                  rows={8}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Email message"
                />
              </div>

              {/* Additional Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional Recipients (CC)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={additionalRecipient}
                    onChange={(e) => setAdditionalRecipient(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Enter email address"
                  />
                  <button
                    onClick={handleAddRecipient}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    Add
                  </button>
                </div>
                {emailData.additionalRecipients && emailData.additionalRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {emailData.additionalRecipients.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveRecipient(email)}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !emailData.subject.trim() || !emailData.body.trim()}
              className="btn-primary w-full inline-flex justify-center items-center sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900 mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCompanyModal;

